import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import color from 'src/theme/color';

import { ReactComponent as GithubIcon } from '../../../../../../src/assets/icons/github.svg';

import { IconBox, IconBoxContainer, ConnectedLabel } from '../../styled';
import { getProjectDetailsFromState } from '../../../../../redux/onboarding/selectors';
import useAppDispatch from '../../../../../hooks/useAppDispatch';
import { getGithubAppUrl } from '../../../../../redux/onboarding/service';

interface GitHubServiceProps {
    setLoading: (loading: boolean) => void;
}

const GitHubService: FunctionComponent<GitHubServiceProps> = ({
    setLoading
}) => {
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const projectDetails = useSelector(getProjectDetailsFromState);

    useEffect(() => {
        const isGithubConnected = projectDetails.details.githubConnected;
        if (isGithubConnected) {
            setError(null);
            setConnected(isGithubConnected);
        }

        const githubAppUrl = projectDetails.details.githubAppUrl;
        if (githubAppUrl && !connected) {
            window.open(
                githubAppUrl,
                '_blank',
                'width=800,height=600,scrollbars=yes,resizable=yes'
            );
        }
    }, [projectDetails, connected]);

    const handleClick = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await dispatch(
                getGithubAppUrl({ projectId: projectDetails.details.projectId })
            );

            // Check if the action was rejected
            if (getGithubAppUrl.rejected.match(result)) {
                // If payload is null, check for missing projectId or generic error
                if (result.meta?.arg?.projectId == null) {
                    setError(
                        'Project ID is missing. Your token may have expired. Please try again from the beginning.'
                    );
                } else {
                    setError('Failed to get GitHub app URL');
                }
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <IconBoxContainer>
            <IconBox
                onClick={handleClick}
                className={connected ? 'selected' : ''}
            >
                <GithubIcon style={{ width: '45px', height: '45px' }} />
                <Typography
                    color={color.white}
                    mt={3}
                    sx={{
                        fontWeight: 'bold'
                    }}
                >
                    GitHub
                </Typography>
                {connected && <ConnectedLabel>Connected</ConnectedLabel>}
                {error && (
                    <Typography
                        color={color.red || '#f44336'}
                        sx={{
                            fontSize: '12px',
                            fontWeight: '400',
                            marginTop: '8px',
                            textAlign: 'center'
                        }}
                    >
                        {error}
                    </Typography>
                )}
            </IconBox>
        </IconBoxContainer>
    );
};

export default GitHubService;
